import "./styles.css";
import { useRef, useState, useEffect } from "react";
import { IoClose } from "react-icons/io5";
import { useTranslation } from "react-i18next";
import { TabularCard } from "../TabularCard/TabularCard";
import {
  GeneralInfoAi,
  ResultBasedInfoAi,
  TabularInfoAiBar,
} from "./Utilities";
import { useConfig } from "../../context/ConfigContext";
import { useUID } from "../../context/UIDContext";
import { useAmplitude } from "../../context/AmplitudeContext";
import { convertStrToSlug } from "../../services/utilitiesService";
import { MdInfoOutline } from "react-icons/md";
import { PercentageBar } from "../PercentageBar/PercentageBar";
import { useScanImage } from "../../context/ImageContext";
import { HeadMeshStatic } from "../animations/HeadMesh/HeadMeshStatic";

export const Results = ({ results, onProductClick, onExitClick }) => {
  const { t } = useTranslation(["aiResults", "customAiInfo"]);
  const { frontAnimationImage } = useScanImage();
  const detailTextRef = useRef(null);
  const [selectedResult, setSelectedResult] = useState(Object.keys(results)[0]);
  const { uid } = useUID();
  const trackEvent = useAmplitude();
  const { configData } = useConfig();

  useEffect(() => {
    // Track user visits Results Page
    trackEvent("webapp_user_visited_results_page", uid, "results");
  }, [uid, trackEvent, configData.recommendationEnabled]);

  const loadShortInfo = () => {
    switch (selectedResult) {
      case "Hair Color":
        return results[selectedResult].color_info.length > 0 ? (
          <div className="hair-color-bar">
            {results[selectedResult].color_info.map((shades, index) => (
              <div
                key={index}
                className={
                  index === 0 ? "hair-color-item dominant" : "hair-color-item"
                }
                style={{
                  backgroundColor: `${shades.hex_code}`,
                }}
              />
            ))}
          </div>
        ) : (
          <h4 className="result-decision">{t("No hair")}</h4>
        );
      case "Hair Gloss":
        return results[selectedResult].percentages &&
          Object.keys(results[selectedResult].percentages).length > 0 ? (
          <PercentageBar
            progress={results[selectedResult].percentages.Glossy}
          />
        ) : (
          <h4 className="result-decision">{t("No hair")}</h4>
        );
      case "Hair Loss":
        return (
          <h4 className="result-decision">
            {t(`hair-loss-${convertStrToSlug(results[selectedResult].value)}`)}
          </h4>
        );
      default:
        return (
          <h4 className="result-decision">
            {t(results[selectedResult].value)}
          </h4>
        );
    }
  };

  const loadResultCategories = () => {
    const resultNames = [];
    for (const key in results) {
      resultNames.push(key);
    }

    return resultNames.map((resultName, index) => (
      <button
        className={`result-button ${
          resultName === selectedResult ? "selected" : ""
        }`}
        key={index}
        id={convertStrToSlug(resultName)}
        onClick={() => setSelectedResult(resultName)}
      >
        {t(resultName)}
      </button>
    ));
  };

  const scrollToDetailText = () => {
    if (detailTextRef.current) {
      detailTextRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  return (
    <div className="result">
      <div className="result-short-info">
        <div className="image-overlay">
          <HeadMeshStatic
            imageUrl={frontAnimationImage.imageUrl || ""}
            maskUrl={
              `data:image/png;base64, ${frontAnimationImage.maskUrl}` || ""
            }
            width={frontAnimationImage.width}
            height={frontAnimationImage.height}
          />
        </div>
        <div className="results-close-wrapper">
          <IoClose
            className="results-close-icon"
            onClick={() => {
              trackEvent("webapp_user_pressed_results_close_icon", uid);
              onExitClick();
            }}
          />
        </div>
        <div className="result-info-container">
          <div className="results-info">
            <div className="results-header-container">
              {selectedResult === "Hair Gloss" &&
                results[selectedResult].percentages &&
                Object.keys(results[selectedResult].percentages).length > 0 && (
                  <p>Stumpf</p>
                )}
              <div className="result-type" onClick={scrollToDetailText}>
                <p>{t(selectedResult)}</p>
                <MdInfoOutline />
              </div>
              {selectedResult === "Hair Gloss" &&
                results[selectedResult].percentages &&
                Object.keys(results[selectedResult].percentages).length > 0 && (
                  <p>Glanz</p>
                )}
            </div>
            <div className="result-decision-container">{loadShortInfo()}</div>
          </div>
        </div>
        <div className="result-types">
          <div
            className="result-types-button-wrapper"
            onTouchStart={(event) => event.stopPropagation()}
          >
            {loadResultCategories()}
          </div>
        </div>
        <div className="result-bottom-buttons-container">
          {configData.recommendationEnabled && (
            <button
              className="recommend-button"
              onClick={() => {
                trackEvent("webapp_user_press_show_products", uid);
                onProductClick();
              }}
            >
              {t("results-nav-button")}
            </button>
          )}
        </div>
        <div className="text-info-background" />
      </div>
      <div ref={detailTextRef} className="result-detail-info">
        {results[selectedResult].percentages ? (
          <TabularCard tabNames={TabularInfoAiBar[selectedResult]} />
        ) : (
          <p className="result-detail-text">
            {t(
              GeneralInfoAi.includes(selectedResult)
                ? "customAiInfo:" +
                    selectedResult.toLowerCase().split(" ").join("-") +
                    "-info"
                : ResultBasedInfoAi.includes(selectedResult)
                  ? "customAiInfo:" +
                    selectedResult.toLowerCase().split(" ").join("-") +
                    "-" +
                    results[selectedResult].value
                      .toLowerCase()
                      .split(" ")
                      .join("-") +
                    "-info"
                  : "customAiInfo:" +
                    results[selectedResult].value
                      .toLowerCase()
                      .split(" ")
                      .join("-") +
                    "-info",
            )}
          </p>
        )}
      </div>
    </div>
  );
};
